import React, { useState } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Button, Container, Box, Link, Stack,
  IconButton, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider,
  useScrollTrigger, Menu, MenuItem, Collapse
} from '@mui/material';

// Core Navigation Icons
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import NotesIcon from '@mui/icons-material/Notes';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

// Service Sub-Route Icons
import AndroidIcon from '@mui/icons-material/Android';
import PhonelinkIcon from '@mui/icons-material/Phonelink';
import WebIcon from '@mui/icons-material/Web';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DevicesIcon from '@mui/icons-material/Devices';
import CodeIcon from '@mui/icons-material/Code';


const serviceSubItems = [
  { name: 'All Services Overview', path: '/services', icon: <SettingsOutlinedIcon fontSize="small" /> },
  { name: 'Android Development', path: '/services/android-development', icon: <AndroidIcon fontSize="small" /> },
  { name: 'Flutter Development', path: '/services/flutter-development', icon: <PhonelinkIcon fontSize="small" /> },
  { name: 'React Native', path: '/services/react-native', icon: <CodeIcon fontSize="small" /> },
  { name: 'Hybrid App Dev', path: '/services/hybrid-app-development', icon: <DevicesIcon fontSize="small" /> },
  { name: 'Web Development', path: '/services/web-development', icon: <WebIcon fontSize="small" /> },
  { name: 'IT Consulting', path: '/services/it-consulting', icon: <AssessmentIcon fontSize="small" /> },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesAnchorEl, setServicesAnchorEl] = useState(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  const location = useLocation();
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 20 });

  const toggleDrawer = (open) => () => {
    setMobileOpen(open);
    if (!open) {
      setMobileServicesOpen(false);
    }
  };

  const handleServicesMenuOpen = (event) => {
    setServicesAnchorEl(event.currentTarget);
  };

  const handleServicesMenuClose = () => {
    setServicesAnchorEl(null);
  };

  const handleMobileServicesToggle = (e) => {
    e.stopPropagation();
    setMobileServicesOpen((prev) => !prev);
  };

  const navItems = [
    { name: 'Home', icon: <HomeOutlinedIcon sx={{ fontSize: 18 }} />, path: '/' },
    { name: 'About', icon: <PersonOutlineOutlinedIcon sx={{ fontSize: 18 }} />, path: '/about' },
    {
      name: 'Services',
      icon: <SettingsOutlinedIcon sx={{ fontSize: 18 }} />,
      path: '/services',
      isDropdown: true
    },
    { name: 'Portfolio', icon: <BusinessCenterOutlinedIcon sx={{ fontSize: 18 }} />, path: '/portfolio' },
    { name: 'Blog', icon: <NotesIcon sx={{ fontSize: 18 }} />, path: '/blog' },
    { name: 'Contact', icon: <CallOutlinedIcon sx={{ fontSize: 18 }} />, path: '/contact' },
  ];

  const isServiceActive = location.pathname.startsWith('/services');

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: trigger ? 'rgba(255, 255, 255, 0.92)' : 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(16px)',
          borderBottom: trigger ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid transparent',
          boxShadow: trigger ? '0 10px 30px -10px rgba(0, 0, 0, 0.05)' : 'none',
          color: '#0f172a',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            variant="dense"
            disableGutters
            sx={{
              justifyContent: 'space-between',
              minHeight: trigger ? '60px !important' : '72px !important',
              transition: 'all 0.3s ease-in-out',
              px: { xs: 2, sm: 0 }
            }}
          >
            {/* Brand Logo */}
            <Typography
              variant="h6"
              component={RouterLink}
              to="/"
              sx={{
                fontWeight: 900,
                textDecoration: 'none',
                color: '#0f172a',
                letterSpacing: '-0.02em',
                fontSize: '1.35rem',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              Vikash<span style={{ color: '#2563eb' }}> Tiwari</span>
            </Typography>

            {/* Desktop Navigation Links */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
              {navItems.map((item) => {
                if (item.isDropdown) {
                  return (
                    <React.Fragment key={item.name}>
                      <Button
                        startIcon={item.icon}
                        endIcon={
                          <KeyboardArrowDownIcon
                            sx={{
                              fontSize: '16px !important',
                              transition: 'transform 0.2s',
                              transform: Boolean(servicesAnchorEl) ? 'rotate(180deg)' : 'none'
                            }}
                          />
                        }
                        onClick={handleServicesMenuOpen}
                        sx={{
                          color: isServiceActive ? '#2563eb' : '#475569',
                          background: isServiceActive ? 'rgba(37, 99, 235, 0.08)' : 'transparent',
                          fontWeight: isServiceActive ? 700 : 600,
                          fontSize: '0.875rem',
                          textTransform: 'none',
                          mx: 0.5, px: 2, py: 0.8,
                          borderRadius: '10px',
                          border: isServiceActive ? '1px solid rgba(37, 99, 235, 0.2)' : '1px solid transparent',
                          '&:hover': {
                            color: '#0f172a',
                            background: 'rgba(0, 0, 0, 0.04)',
                            borderColor: 'rgba(0, 0, 0, 0.08)'
                          }
                        }}
                      >
                        {item.name}
                      </Button>

                      <Menu
                        anchorEl={servicesAnchorEl}
                        open={Boolean(servicesAnchorEl)}
                        onClose={handleServicesMenuClose}
                        PaperProps={{
                          elevation: 0,
                          sx: {
                            mt: 1.5,
                            minWidth: 240,
                            borderRadius: '16px',
                            background: '#ffffff',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.08)',
                            p: 1,
                            '& .MuiList-root': { py: 0 }
                          }
                        }}
                        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                      >
                        {serviceSubItems.map((subItem) => {
                          const isSubActive = location.pathname === subItem.path;
                          return (
                            <MenuItem
                              key={subItem.path}
                              component={RouterLink}
                              to={subItem.path}
                              onClick={handleServicesMenuClose}
                              selected={isSubActive}
                              sx={{
                                borderRadius: '10px',
                                my: 0.4,
                                py: 1,
                                px: 1.5,
                                fontSize: '0.85rem',
                                fontWeight: isSubActive ? 700 : 500,
                                color: isSubActive ? '#2563eb' : '#334155',
                                '&.Mui-selected': {
                                  bgcolor: 'rgba(37, 99, 235, 0.08)',
                                  color: '#2563eb',
                                  '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.12)' }
                                },
                                '&:hover': {
                                  bgcolor: 'rgba(0, 0, 0, 0.03)',
                                  color: '#0f172a'
                                }
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 32, color: isSubActive ? '#2563eb' : '#64748b' }}>
                                {subItem.icon}
                              </ListItemIcon>
                              {subItem.name}
                            </MenuItem>
                          );
                        })}
                      </Menu>
                    </React.Fragment>
                  );
                }

                const isActive = location.pathname === item.path;
                return (
                  <Button
                    key={item.name}
                    component={RouterLink}
                    to={item.path}
                    startIcon={item.icon}
                    sx={{
                      color: isActive ? '#2563eb' : '#475569',
                      background: isActive ? 'rgba(37, 99, 235, 0.08)' : 'transparent',
                      fontWeight: isActive ? 700 : 600,
                      fontSize: '0.875rem',
                      textTransform: 'none',
                      mx: 0.5, px: 2, py: 0.8,
                      borderRadius: '10px',
                      border: isActive ? '1px solid rgba(37, 99, 235, 0.2)' : '1px solid transparent',
                      '&:hover': {
                        color: '#0f172a',
                        background: 'rgba(0, 0, 0, 0.04)',
                        borderColor: 'rgba(0, 0, 0, 0.08)'
                      }
                    }}
                  >
                    {item.name}
                  </Button>
                );
              })}

              <Box sx={{ width: '1px', height: '24px', bgcolor: 'rgba(0,0,0,0.08)', mx: 2 }} />

              <Stack direction="row" spacing={1}>
                <IconButton
                  component="a"
                  href="mailto:vikash15112000@gmail.com"
                  size="small"
                  sx={{ color: '#64748b', border: '1px solid rgba(0, 0, 0, 0.08)', borderRadius: '10px', '&:hover': { color: '#2563eb', bgcolor: 'rgba(37, 99, 235, 0.08)' } }}
                >
                  <EmailOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  component="a"
                  href="https://github.com/tiwarivk1511"
                  target="_blank"
                  size="small"
                  sx={{ color: '#64748b', border: '1px solid rgba(0, 0, 0, 0.08)', borderRadius: '10px', '&:hover': { color: '#2563eb', bgcolor: 'rgba(37, 99, 235, 0.08)' } }}
                >
                  <GitHubIcon fontSize="small" />
                </IconButton>
                <IconButton
                  component="a"
                  href="https://linkedin.com/in/vikash-tiwari15112000"
                  target="_blank"
                  size="small"
                  sx={{ color: '#64748b', border: '1px solid rgba(0, 0, 0, 0.08)', borderRadius: '10px', '&:hover': { color: '#2563eb', bgcolor: 'rgba(37, 99, 235, 0.08)' } }}
                >
                  <LinkedInIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>

            {/* Mobile Menu Trigger Button */}
            <IconButton
              size="small"
              onClick={toggleDrawer(true)}
              sx={{
                color: "#0f172a",
                display: { xs: 'flex', md: 'none' },
                bgcolor: 'rgba(0, 0, 0, 0.03)',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                p: 1,
                borderRadius: '10px'
              }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Spacer to push page body content below fixed navbar */}
      <Box sx={{ height: { xs: '60px', md: '72px' } }} />

      {/* Mobile Drawer (Structured via Flexbox to prevent layout collisions) */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: '300px',
            bgcolor: '#ffffff',
            color: '#0f172a',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
          }
        }}
      >
        {/* Drawer Header */}
        <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
          <Typography variant="h6" sx={{ fontWeight: 900, color: '#2563eb', letterSpacing: 0.5, fontSize: '1.1rem' }}>
            Menu
          </Typography>
          <IconButton onClick={toggleDrawer(false)} size="small" sx={{ color: '#64748b' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Scrollable Navigation Body */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, py: 2 }}>
          <List disablePadding>
            {navItems.map((item) => {
              if (item.isDropdown) {
                return (
                  <React.Fragment key={item.name}>
                    <ListItem disablePadding sx={{ mb: 0.8 }}>
                      <ListItemButton
                        onClick={handleMobileServicesToggle}
                        sx={{
                          borderRadius: '12px',
                          bgcolor: isServiceActive ? 'rgba(37, 99, 235, 0.08)' : 'rgba(0, 0, 0, 0.02)',
                          border: isServiceActive ? '1px solid rgba(37, 99, 235, 0.2)' : '1px solid rgba(0, 0, 0, 0.05)',
                          color: isServiceActive ? '#2563eb' : '#334155',
                          py: 1.2
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: '36px', color: isServiceActive ? '#2563eb' : '#64748b' }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.name}
                          primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: isServiceActive ? 700 : 600 }}
                        />
                        {mobileServicesOpen ? <ExpandLess sx={{ color: '#64748b' }} /> : <ExpandMore sx={{ color: '#64748b' }} />}
                      </ListItemButton>
                    </ListItem>

                    <Collapse in={mobileServicesOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ pl: 1.5, mb: 1 }}>
                        {serviceSubItems.map((subItem) => {
                          const isSubActive = location.pathname === subItem.path;
                          return (
                            <ListItemButton
                              key={subItem.path}
                              component={RouterLink}
                              to={subItem.path}
                              onClick={toggleDrawer(false)}
                              sx={{
                                borderRadius: '10px',
                                my: 0.4,
                                py: 1,
                                bgcolor: isSubActive ? 'rgba(37, 99, 235, 0.1)' : 'transparent',
                                color: isSubActive ? '#2563eb' : '#475569',
                                '&:hover': { color: '#0f172a' }
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: '32px', color: isSubActive ? '#2563eb' : '#94a3b8' }}>
                                {subItem.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={subItem.name}
                                primaryTypographyProps={{ fontSize: '0.825rem', fontWeight: isSubActive ? 700 : 500 }}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              }

              const isActive = location.pathname === item.path;
              return (
                <ListItem key={item.name} disablePadding sx={{ mb: 0.8 }}>
                  <ListItemButton
                    component={RouterLink}
                    to={item.path}
                    onClick={toggleDrawer(false)}
                    sx={{
                      borderRadius: '12px',
                      bgcolor: isActive ? 'rgba(37, 99, 235, 0.08)' : 'transparent',
                      border: isActive ? '1px solid rgba(37, 99, 235, 0.2)' : '1px solid transparent',
                      color: isActive ? '#2563eb' : '#334155',
                      py: 1.2
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: '36px', color: isActive ? '#2563eb' : '#64748b' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.name}
                      primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: isActive ? 700 : 600 }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>

        {/* Static Footer Section (Pinned to bottom of drawer) */}
        <Box sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.08)', bgcolor: '#f8fafc' }}>
          <Box
            sx={{
              p: 2,
              textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.06) 0%, rgba(37, 99, 235, 0.12) 100%)',
              border: '1px solid rgba(37, 99, 235, 0.2)',
              borderRadius: '16px',
            }}
          >
            <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600, color: '#1e293b', fontSize: '0.825rem' }}>
              Connect with me
            </Typography>
            <Stack direction="row" spacing={1.5} justifyContent="center">
              <IconButton
                component="a"
                href="mailto:vikash15112000@gmail.com"
                size="small"
                sx={{ color: '#2563eb', bgcolor: '#ffffff', boxShadow: '0 2px 6px rgba(0,0,0,0.06)', '&:hover': { color: '#ffffff', bgcolor: '#2563eb' } }}
              >
                <EmailOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton
                component="a"
                href="https://github.com/tiwarivk1511"
                target="_blank"
                size="small"
                sx={{ color: '#2563eb', bgcolor: '#ffffff', boxShadow: '0 2px 6px rgba(0,0,0,0.06)', '&:hover': { color: '#ffffff', bgcolor: '#2563eb' } }}
              >
                <GitHubIcon fontSize="small" />
              </IconButton>
              <IconButton
                component="a"
                href="https://linkedin.com/in/vikash-tiwari15112000"
                target="_blank"
                size="small"
                sx={{ color: '#2563eb', bgcolor: '#ffffff', boxShadow: '0 2px 6px rgba(0,0,0,0.06)', '&:hover': { color: '#ffffff', bgcolor: '#2563eb' } }}
              >
                <LinkedInIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;

// import React, { useState } from 'react';
// import { useLocation, Link as RouterLink } from 'react-router-dom';
// import {
//   AppBar, Toolbar, Typography, Button, Container, Box, Link, Stack,
//   IconButton, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider,
//   useScrollTrigger, Menu, MenuItem, Collapse
// } from '@mui/material';

// // Core Navigation Icons
// import MenuIcon from '@mui/icons-material/Menu';
// import CloseIcon from '@mui/icons-material/Close';
// import GitHubIcon from '@mui/icons-material/GitHub';
// import LinkedInIcon from '@mui/icons-material/LinkedIn';
// import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
// import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
// import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
// import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
// import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
// import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
// import NotesIcon from '@mui/icons-material/Notes';
// import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import ExpandLess from '@mui/icons-material/ExpandLess';
// import ExpandMore from '@mui/icons-material/ExpandMore';

// // Service Sub-Route Icons
// import AndroidIcon from '@mui/icons-material/Android';
// import PhonelinkIcon from '@mui/icons-material/Phonelink';
// import WebIcon from '@mui/icons-material/Web';
// import AssessmentIcon from '@mui/icons-material/Assessment';
// import DevicesIcon from '@mui/icons-material/Devices';
// import CodeIcon from '@mui/icons-material/Code';

// const serviceSubItems = [
//   { name: 'All Services Overview', path: '/services', icon: <SettingsOutlinedIcon fontSize="small" /> },
//   { name: 'Android Development', path: '/services/android-development', icon: <AndroidIcon fontSize="small" /> },
//   { name: 'Flutter Development', path: '/services/flutter-development', icon: <PhonelinkIcon fontSize="small" /> },
//   { name: 'React Native', path: '/services/react-native', icon: <CodeIcon fontSize="small" /> },
//   { name: 'Hybrid App Dev', path: '/services/hybrid-app-development', icon: <DevicesIcon fontSize="small" /> },
//   { name: 'Web Development', path: '/services/web-development', icon: <WebIcon fontSize="small" /> },
//   { name: 'IT Consulting', path: '/services/it-consulting', icon: <AssessmentIcon fontSize="small" /> },
// ];

// const Navbar = () => {
//   const [mobileOpen, setMobileOpen] = useState(false);
//   const [servicesAnchorEl, setServicesAnchorEl] = useState(null);
//   const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

//   const location = useLocation();
//   const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 20 });

//   const toggleDrawer = (open) => () => {
//     setMobileOpen(open);
//     if (!open) setMobileServicesOpen(false);
//   };

//   const handleServicesMenuOpen = (event) => {
//     setServicesAnchorEl(event.currentTarget);
//   };

//   const handleServicesMenuClose = () => {
//     setServicesAnchorEl(null);
//   };

//   const handleMobileServicesToggle = () => {
//     setMobileServicesOpen((prev) => !prev);
//   };

//   const navItems = [
//     { name: 'Home', icon: <HomeOutlinedIcon sx={{ fontSize: 18 }} />, path: '/' },
//     { name: 'About', icon: <PersonOutlineOutlinedIcon sx={{ fontSize: 18 }} />, path: '/about' },
//     {
//       name: 'Services',
//       icon: <SettingsOutlinedIcon sx={{ fontSize: 18 }} />,
//       path: '/services',
//       isDropdown: true
//     },
//     { name: 'Portfolio', icon: <BusinessCenterOutlinedIcon sx={{ fontSize: 18 }} />, path: '/portfolio' },
//     { name: 'Blog', icon: <NotesIcon sx={{ fontSize: 18 }} />, path: '/blog' },
//     { name: 'Contact', icon: <CallOutlinedIcon sx={{ fontSize: 18 }} />, path: '/contact' },
//   ];

//   const isServiceActive = location.pathname.startsWith('/services');

//   return (
//     <>
//       <AppBar
//         position="fixed"
//         sx={{
//           background: trigger ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.95)',
//           backdropFilter: 'blur(15px)',
//           boxShadow: trigger ? '0 4px 20px rgba(0, 0, 0, 0.08)' : 'none',
//           color: '#333',
//           transition: '0.3s ease-in-out',
//         }}
//       >
//         <Container maxWidth="lg">
//           <Toolbar
//             variant="dense"
//             sx={{
//               justify: 'space-between',
//               minHeight: trigger ? '55px !important' : '70px !important',
//               transition: '0.3s ease-in-out',
//             }}
//           >
//             {/* Logo */}
//             <Typography
//               variant="h5"
//               component={RouterLink}
//               to="/"
//               sx={{ fontWeight: 900, textDecoration: 'none', color: 'inherit', flexGrow: { xs: 1, md: 0 } }}
//             >
//               Vikash<span style={{ color: '#1976d2' }}>Tiwari</span>
//             </Typography>

//             {/* Desktop Navigation */}
//             <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
//               {navItems.map((item) => {
//                 if (item.isDropdown) {
//                   return (
//                     <React.Fragment key={item.name}>
//                       <Button
//                         startIcon={item.icon}
//                         endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '16px !important' }} />}
//                         onClick={handleServicesMenuOpen}
//                         sx={{
//                           color: isServiceActive ? '#1976d2' : '#555',
//                           background: isServiceActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
//                           fontWeight: isServiceActive ? 800 : 600,
//                           fontSize: '0.85rem',
//                           textTransform: 'none',
//                           mx: 0.5, px: 2, borderRadius: '10px',
//                           '&:hover': { background: 'rgba(25, 118, 210, 0.04)' }
//                         }}
//                       >
//                         {item.name}
//                       </Button>

//                       <Menu
//                         anchorEl={servicesAnchorEl}
//                         open={Boolean(servicesAnchorEl)}
//                         onClose={handleServicesMenuClose}
//                         PaperProps={{
//                           elevation: 4,
//                           sx: {
//                             mt: 1.5,
//                             minWidth: 230,
//                             borderRadius: '12px',
//                             boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
//                             p: 1,
//                             bgcolor: '#ffffff', // Explicitly White Background
//                           }
//                         }}
//                         transformOrigin={{ horizontal: 'left', vertical: 'top' }}
//                         anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
//                       >
//                         {serviceSubItems.map((subItem) => {
//                           const isSubActive = location.pathname === subItem.path;
//                           return (
//                             <MenuItem
//                               key={subItem.path}
//                               component={RouterLink}
//                               to={subItem.path}
//                               onClick={handleServicesMenuClose}
//                               selected={isSubActive}
//                               sx={{
//                                 borderRadius: '8px',
//                                 my: 0.3,
//                                 py: 1,
//                                 fontSize: '0.85rem',
//                                 fontWeight: isSubActive ? 700 : 500,
//                                 color: isSubActive ? '#1976d2' : '#222222', // Inactive text ko dark & readable banaya
//                                 '&.Mui-selected': {
//                                   bgcolor: 'rgba(25, 118, 210, 0.1)',
//                                   color: '#1976d2',
//                                 },
//                                 '&:hover': {
//                                   bgcolor: isSubActive ? 'rgba(25, 118, 210, 0.15)' : 'rgba(0, 0, 0, 0.04)',
//                                 }
//                               }}
//                             >
//                               <ListItemIcon sx={{ minWidth: 32, color: isSubActive ? '#1976d2' : '#555555' }}>
//                                 {subItem.icon}
//                               </ListItemIcon>
//                               {subItem.name}
//                             </MenuItem>
//                           );
//                         })}
//                       </Menu>
//                     </React.Fragment>
//                   );
//                 }

//                 const isActive = location.pathname === item.path;
//                 return (
//                   <Button
//                     key={item.name}
//                     component={RouterLink}
//                     to={item.path}
//                     startIcon={item.icon}
//                     sx={{
//                       color: isActive ? '#1976d2' : '#555',
//                       background: isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
//                       fontWeight: isActive ? 800 : 600,
//                       fontSize: '0.85rem',
//                       textTransform: 'none',
//                       mx: 0.5, px: 2, borderRadius: '10px',
//                       '&:hover': { background: 'rgba(25, 118, 210, 0.04)' }
//                     }}
//                   >
//                     {item.name}
//                   </Button>
//                 );
//               })}

//               <Box sx={{ width: '1px', height: '20px', bgcolor: 'rgba(0,0,0,0.1)', mx: 1.5 }} />

//               <Stack direction="row" spacing={0.5}>
//                 <IconButton href="mailto:vikash15112000@gmail.com" size="small" sx={{ color: '#333', '&:hover': { color: '#1976d2' } }}>
//                   <EmailOutlinedIcon fontSize="inherit" />
//                 </IconButton>
//                 <IconButton href="https://github.com/tiwarivk1511" target="_blank" size="small" sx={{ color: '#333', '&:hover': { color: '#1976d2' } }}>
//                   <GitHubIcon fontSize="inherit" />
//                 </IconButton>
//                 <IconButton href="https://linkedin.com/in/vikash-tiwari15112000" target="_blank" size="small" sx={{ color: '#333', '&:hover': { color: '#1976d2' } }}>
//                   <LinkedInIcon fontSize="inherit" />
//                 </IconButton>
//               </Stack>
//             </Box>

//             {/* Mobile Drawer Trigger */}
//             <IconButton size="small" sx={{ color: "#1976d2", display: { xs: 'flex', md: 'none' } }} onClick={toggleDrawer(true)}>
//               <MenuIcon fontSize="medium" />
//             </IconButton>
//           </Toolbar>
//         </Container>
//       </AppBar>

//       <Box sx={{ height: { xs: '55px', md: '70px' } }} />

//       {/* Drawer for Mobile */}
//       <Drawer anchor="right" open={mobileOpen} onClose={toggleDrawer(false)} PaperProps={{ sx: { width: '280px' } }}>
//         <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
//           <Typography variant="h6" sx={{ fontWeight: 900, color: '#1976d2' }}>MENU</Typography>
//           <IconButton onClick={toggleDrawer(false)} size="small"><CloseIcon /></IconButton>
//         </Box>
//         <Divider />
//         <List sx={{ px: 1.5, py: 2 }}>
//           {navItems.map((item) => {
//             if (item.isDropdown) {
//               return (
//                 <React.Fragment key={item.name}>
//                   <ListItem disablePadding sx={{ mb: 0.5 }}>
//                     <ListItemButton
//                       onClick={handleMobileServicesToggle}
//                       sx={{
//                         borderRadius: '12px',
//                         bgcolor: isServiceActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
//                         color: isServiceActive ? '#1976d2' : 'inherit',
//                       }}
//                     >
//                       <ListItemIcon sx={{ minWidth: '40px', color: isServiceActive ? '#1976d2' : '#777' }}>
//                         {item.icon}
//                       </ListItemIcon>
//                       <ListItemText primary={item.name} primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: isServiceActive ? 800 : 600 }} />
//                       {mobileServicesOpen ? <ExpandLess /> : <ExpandMore />}
//                     </ListItemButton>
//                   </ListItem>

//                   <Collapse in={mobileServicesOpen} timeout="auto" unmountOnExit>
//                     <List component="div" disablePadding sx={{ pl: 2 }}>
//                       {serviceSubItems.map((subItem) => {
//                         const isSubActive = location.pathname === subItem.path;
//                         return (
//                           <ListItemButton
//                             key={subItem.path}
//                             component={RouterLink}
//                             to={subItem.path}
//                             onClick={toggleDrawer(false)}
//                             sx={{
//                               borderRadius: '10px',
//                               my: 0.3,
//                               py: 0.8,
//                               bgcolor: isSubActive ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
//                               color: isSubActive ? '#1976d2' : '#555'
//                             }}
//                           >
//                             <ListItemIcon sx={{ minWidth: '32px', color: isSubActive ? '#1976d2' : '#888' }}>
//                               {subItem.icon}
//                             </ListItemIcon>
//                             <ListItemText
//                               primary={subItem.name}
//                               primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: isSubActive ? 700 : 500 }}
//                             />
//                           </ListItemButton>
//                         );
//                       })}
//                     </List>
//                   </Collapse>
//                 </React.Fragment>
//               );
//             }

//             const isActive = location.pathname === item.path;
//             return (
//               <ListItem key={item.name} disablePadding sx={{ mb: 1 }}>
//                 <ListItemButton
//                   component={RouterLink}
//                   to={item.path}
//                   onClick={toggleDrawer(false)}
//                   sx={{
//                     borderRadius: '12px',
//                     bgcolor: isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
//                     color: isActive ? '#1976d2' : 'inherit',
//                   }}
//                 >
//                   <ListItemIcon sx={{ minWidth: '40px', color: isActive ? '#1976d2' : '#777' }}>
//                     {item.icon}
//                   </ListItemIcon>
//                   <ListItemText primary={item.name} primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: isActive ? 800 : 600 }} />
//                 </ListItemButton>
//               </ListItem>
//             );
//           })}
//         </List>

//         {/* Floating Social Card in Drawer */}
//         <Box sx={{
//           position: 'absolute',
//           bottom: 20,
//           left: '50%',
//           transform: 'translateX(-50%)',
//           width: '85%',
//           p: 2,
//           textAlign: 'center',
//           background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
//           borderRadius: '20px',
//           boxShadow: '0 15px 35px rgba(25, 118, 210, 0.4)',
//           color: '#fff'
//         }}>
//           <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, opacity: 0.9 }}>Connect with me</Typography>
//           <Stack direction="row" spacing={3} justifyContent="center">
//             <Link href="mailto:vikash15112000@gmail.com" sx={{ color: '#fff' }}><EmailOutlinedIcon /></Link>
//             <Link href="https://github.com/tiwarivk1511" target="_blank" sx={{ color: '#fff' }}><GitHubIcon /></Link>
//             <Link href="https://linkedin.com/in/vikash-tiwari15112000" target="_blank" sx={{ color: '#fff' }}><LinkedInIcon /></Link>
//           </Stack>
//         </Box>
//       </Drawer>
//     </>
//   );
// };

// export default Navbar;
